import type { SupportTicketMergeEventData } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event";

export interface SupportTicketMergeStateEvent {
  readonly id: string;
  readonly eventType: "support_ticket_merged" | "support_ticket_merge_undone";
  readonly eventData: SupportTicketMergeEventData;
  readonly occurredAt: Date;
}

export function getSupportTicketMergePairKey(
  data: SupportTicketMergeEventData,
): string {
  return [data.sourceSupportTicketId, data.destinationSupportTicketId].join(
    ":",
  );
}

export function getSupportTicketMergePrimaryKey(
  data: SupportTicketMergeEventData,
): string {
  return data.relationshipId ?? getSupportTicketMergePairKey(data);
}

export function getSupportTicketMergeEventKeys(
  data: SupportTicketMergeEventData,
): string[] {
  const pairKey = getSupportTicketMergePairKey(data);
  // New merge events carry the exact topic_relationship id. Older events do
  // not, so source/destination ids are the compatibility key for those rows.
  return data.relationshipId ? [data.relationshipId, pairKey] : [pairKey];
}

export function getCurrentSupportTicketMergeEventIds(
  events: readonly SupportTicketMergeStateEvent[],
): Set<string> {
  const latestByKey = new Map<string, string>();
  const chronologicalEvents = [...events].sort(
    (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
  );

  for (const event of chronologicalEvents) {
    for (const key of getSupportTicketMergeEventKeys(event.eventData)) {
      if (event.eventType === "support_ticket_merged") {
        latestByKey.set(key, event.id);
      } else {
        latestByKey.delete(key);
      }
    }
  }

  return new Set(
    chronologicalEvents
      .filter((event) => event.eventType === "support_ticket_merged")
      .filter((event) =>
        getSupportTicketMergeEventKeys(event.eventData).every(
          (key) => latestByKey.get(key) === event.id,
        ),
      )
      .map((event) => event.id),
  );
}
